<template>
  <div :class="{ '-isFocused': isFocused, '-isPulledUp': pullUp }" class="s-wrapper">
    <div v-if="props.label" class="s-label">{{ label }}</div>
    <div :key="`${kRichEditorKey}-${editorKey}`">
      <editor v-model="content" tinymce-script-src="/vendor/tinymce/js/tinymce/tinymce.min.js" :init="editorOptions" :disabled="isDisabled" @blur="handleBlur" @change="handleChange" @focus="handleFocus" @init="setTinymceInstance" />
    </div>
    <div class="s-feedback">
      <KInputError v-if="detailStore" :name="$props.name" :errors="detailStore.errors" :label="label" />

      <div v-if="!maxChars" class="s-counter">{{ $t("global.characters", { count: characterCount }) }}</div>
      <div v-else class="s-counter" :class="{ 'font-weight-bold': characterCount > maxChars, 'text-pink': characterCount > maxChars, 'text-warning': characterCount > warningMaxChars && characterCount < maxChars }">
        {{ `${characterCount}/${maxChars}` }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends string | null">
import Editor from "@tinymce/tinymce-vue"
import { computed, ComputedRef, inject, InjectionKey, ref, watch } from "vue"
import { makeDefaultOptions } from "@/components/KRichTextEditor/tinyMCEOptions"
import { ClozeTypingEvent, ClozeUpdateEvent } from "@/modules/questions/detailStore"
import { DetailPageStore } from "@/store/factories/detailPageStore"
import { default as TinyMCE, RawEditorOptions } from "../../public/vendor/tinymce/js/tinymce/tinymce"

const detailStore = inject("detailStore", null) as DetailPageStore | null
const props = withDefaults(
  defineProps<{
    modelValue: T
    name?: string
    tinymceOptions?: Partial<RawEditorOptions>
    label?: string
    errors?: Record<string, any>
    height?: number
    warningMaxChars?: number
    maxChars?: number
    disabled?: boolean | null
    placeholder?: string
    kRichEditorKey?: any
    pullUp?: boolean // Adds some negative top margin to make it behave like other input fields
  }>(),
  {
    height: 500,
    disabled: null,
    label: "",
    name: undefined,
    warningMaxChars: undefined,
    maxChars: undefined,
    kRichEditorKey: 0,
    pullUp: false,
    placeholder: undefined,
    tinymceOptions: () => ({}),
    errors: () => {
      return {}
    },
  },
)
const editorKey = ref(0)
const emit = defineEmits(["update:modelValue", "input", "change", "clozeUpdate", "clozeTyping"])
const tinymceInstance = ref<typeof TinyMCE | null>(null)
const content = computed({
  get() {
    return props.modelValue
  },
  set(value: T) {
    emit("update:modelValue", value)
  },
})
function getCharacterCount() {
  // @ts-ignore phpstorm can't detect getContent from TinyMCE type
  return tinymceInstance.value?.getContent({ format: "text" }).trim().replace(/\t+/g, " ").length ?? 0
}
const characterCount = computed(() => {
  if (!content.value) {
    return 0
  }
  return getCharacterCount()
})

const isFocused = ref(false)

const form = inject<{ isDisabled: ComputedRef<boolean> } | undefined>(Symbol.for("vuetify:form") as InjectionKey<{ isDisabled: ComputedRef<boolean> } | undefined>, undefined)
const isDisabled = computed(() => {
  if (props.disabled === null) {
    if (!form) {
      return false
    }
    return form.isDisabled.value
  }
  return props.disabled
})

const editorOptions = computed(() => {
  const options = makeDefaultOptions()
  options.disabled = isDisabled.value
  options.height = props.height
  options.placeholder = props.placeholder

  return {
    ...options,
    ...(props.tinymceOptions ?? {}),
    media_url_resolver: function (data: any, resolve: any) {
      const match = data.url.match(/https?:\/\/(www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/)
      if (match) {
        const embedUrl = `https://www.youtube.com/embed/${match[2]}`
        resolve({
          html: `<iframe src="${embedUrl}" frameborder="0" allowfullscreen></iframe>`,
        })
      } else {
        // Let TinyMCE handle other media URLs normally
        resolve(data)
      }
    },
  }
})

const previousEditorOptions = ""
watch([editorOptions], () => {
  const editorOptionsString = JSON.parse(JSON.stringify(editorOptions.value))
  if (editorOptionsString === previousEditorOptions) {
    return
  }
  editorKey.value++
})

let currentEditorContent = ""

function setTinymceInstance(_: any, instance: typeof TinyMCE) {
  instance.on("clozeUpdate", (event: ClozeUpdateEvent[]) => {
    emit("clozeUpdate", event)
  })
  instance.on("clozeTyping", (event: ClozeTypingEvent) => {
    emit("clozeTyping", event)
  })

  tinymceInstance.value = instance
}
function handleFocus() {
  currentEditorContent = content.value || ""
  isFocused.value = true
}
function handleBlur() {
  if (currentEditorContent !== content.value) {
    // We emit the input event here, because this gives us a semi-reliable way to only emit the input event when the user
    // actually changed something and we didn't change content from the outside.
    emit("input")
  }
  isFocused.value = false
}

function handleChange() {
  emit("change")
}
</script>

<style lang="scss" scoped>
.s-wrapper {
  position: relative;
}

.s-feedback {
  margin-top: 4px;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
}

.s-counter {
  text-align: right;
  font-size: 12px;
  color: $color-grey3;
  flex: 1;
}

.s-label {
  font-size: 10.5px;
  color: $color-grey3;
  position: absolute;
  z-index: 999;
  top: -8px;
  padding: 0 5px;
  background: white;
  margin-left: 10px;
  pointer-events: none;
}
.-isPulledUp {
  margin-top: -8px;
}
</style>
